/**
 * The Channels-backed ChatAdapter — fills the backend seam.
 *
 * Actions and history ride REST (`/api/v1/chat*`); live deltas arrive as
 * versioned envelope events on the `world.chat` topic over the ONE shared
 * multiplexed socket (`realtimeSocket`). A reconnect or seq gap refetches
 * the snapshot — events are notifications, the REST surface is the truth.
 * Unread/mention counters are client-side.
 */

import { z } from "zod";

import { ApiError, apiGet, apiPost } from "@/api/client";
import { realtimeSocket, type TopicSignal } from "@/realtime/socket";

import type {
  ChatActionResult,
  ChatAdapter,
  ChatMessage,
  ChatSnapshot,
} from "./types";

const CHAT_TOPIC = "world.chat";
const MESSAGE_CAP = 100;
const ERROR_RETRY_MS = 10_000;

const reactionSchema = z.object({
  emoji: z.string(),
  count: z.number(),
  reactedByViewer: z.boolean(),
});

const replySchema = z.object({
  id: z.string(),
  displayName: z.string(),
  excerpt: z.string(),
  deleted: z.boolean(),
});

const badgeSchema = z.object({
  label: z.string(),
  iconUrl: z.string().nullable().optional(),
});

const titleSchema = z.object({
  label: z.string(),
  color: z.string(),
  backgroundColor: z.string(),
});

const messageSchema = z.object({
  id: z.string(),
  type: z.enum(["chat", "announcement", "big_win"]),
  playerId: z.number().nullable(),
  displayName: z.string(),
  username: z.string().optional(),
  avatarUrl: z.string().nullable().optional(),
  avatarBorderUrl: z.string().nullable().optional(),
  nameColor: z.string().nullable().optional(),
  tierIconUrl: z.string().nullable().optional(),
  badges: z.array(badgeSchema).optional(),
  title: titleSchema.nullable().optional(),
  isStaff: z.boolean().optional(),
  text: z.string(),
  timestamp: z.string(),
  deleted: z.boolean(),
  replyTo: replySchema.nullable().optional(),
  reactions: z.array(reactionSchema).optional(),
  gameName: z.string().nullable().optional(),
  gameImageUrl: z.string().nullable().optional(),
  gameHref: z.string().nullable().optional(),
  multiplier: z.number().nullable().optional(),
});

const snapshotSchema = z.object({
  topic: z.string(),
  room: z.object({
    name: z.string(),
    displayName: z.string(),
    slowModeSeconds: z.number(),
  }),
  onlineCount: z.number().nullable(),
  pinned: messageSchema.nullable(),
  viewer: z.object({
    playerId: z.number().nullable(),
    displayName: z.string().nullable(),
    canSend: z.boolean(),
    banned: z.boolean(),
    reason: z.string().nullable(),
    rateLimitSeconds: z.number(),
    wagerLocked: z.boolean().optional(),
    wagerProgress: z
      .object({
        wagered: z.number(),
        threshold: z.number(),
        percentage: z.number(),
      })
      .nullable()
      .optional(),
  }),
  messages: z.array(messageSchema),
  hasOlder: z.boolean(),
});

const pageSchema = z.object({
  messages: z.array(messageSchema),
  hasOlder: z.boolean(),
});
const sendSchema = z.object({ message: messageSchema });
const reactResultSchema = z.object({
  messageId: z.string(),
  emoji: z.string(),
  count: z.number(),
  reacted: z.boolean(),
});

const messageEventSchema = z.object({ message: messageSchema });
const deletedEventSchema = z.object({ messageId: z.string() });
const reactionEventSchema = z.object({
  messageId: z.string(),
  emoji: z.string(),
  count: z.number(),
  playerId: z.number().nullable(),
  action: z.enum(["added", "removed"]),
});
const pinnedEventSchema = z.object({ message: messageSchema.nullable() });
const presenceEventSchema = z.object({ online: z.number() });

type ServerMessage = z.infer<typeof messageSchema>;

const INITIAL_SNAPSHOT: ChatSnapshot = Object.freeze({
  status: "connecting",
  messages: [],
  canSend: false,
  hasOlder: false,
  loadingOlder: false,
  unreadCount: 0,
  unreadMentions: 0,
  onlineCount: null,
  errorMessage: null,
  pinnedMessage: null,
  banned: false,
  rateLimitSeconds: 0,
});

function describeError(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback;
}

export class ChatBackendAdapter implements ChatAdapter {
  private snapshot: ChatSnapshot = INITIAL_SNAPSHOT;
  private listeners = new Set<() => void>();
  private selfPlayerId: number | null = null;
  private selfDisplayName: string | null = null;
  private connected = false;
  private refreshing = false;
  private unsubscribeTopic: (() => void) | null = null;
  private retryTimer: number | null = null;
  private rateLimitTimer: number | null = null;

  getSnapshot(): ChatSnapshot {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  connect(): void {
    if (this.connected) return;
    this.connected = true;
    this.unsubscribeTopic = realtimeSocket.subscribe(CHAT_TOPIC, (signal) =>
      this.onSignal(signal),
    );
    void this.refresh();
  }

  disconnect(): void {
    this.connected = false;
    this.unsubscribeTopic?.();
    this.unsubscribeTopic = null;
    if (this.retryTimer !== null) {
      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.stopRateLimitCountdown();
  }

  markViewed(): void {
    if (this.snapshot.unreadCount || this.snapshot.unreadMentions) {
      this.patch({ unreadCount: 0, unreadMentions: 0 });
    }
  }

  async sendMessage(
    text: string,
    options: { replyToId?: string },
  ): Promise<ChatActionResult> {
    try {
      const data = await apiPost(
        "/chat/messages",
        {
          text,
          ...(options.replyToId ? { replyToId: options.replyToId } : {}),
        },
        sendSchema,
      );
      // Instant local echo; the WS copy dedupes by id when it arrives.
      this.upsertMessage(this.toMessage(data.message), { countUnread: false });
      return { ok: true };
    } catch (error) {
      return { ok: false, message: this.describeSendError(error) };
    }
  }

  async loadOlder(): Promise<ChatActionResult> {
    if (this.snapshot.loadingOlder) return { ok: true };
    const oldest = this.snapshot.messages[0];
    if (!oldest || !this.snapshot.hasOlder) return { ok: true };
    this.patch({ loadingOlder: true });
    try {
      const data = await apiGet(
        `/chat/messages?before=${encodeURIComponent(oldest.id)}`,
        pageSchema,
      );
      const known = new Set(this.snapshot.messages.map((m) => m.id));
      const older = data.messages
        .filter((m) => !known.has(m.id))
        .map((m) => this.toMessage(m));
      this.patch({
        messages: [...older, ...this.snapshot.messages],
        hasOlder: data.hasOlder,
        loadingOlder: false,
      });
      return { ok: true };
    } catch (error) {
      this.patch({ loadingOlder: false });
      return {
        ok: false,
        message: describeError(error, "Could not load earlier messages."),
      };
    }
  }

  async react(messageId: string, emoji: string): Promise<ChatActionResult> {
    try {
      const data = await apiPost(
        `/chat/messages/${messageId}/reactions`,
        { emoji },
        reactResultSchema,
      );
      this.applyReaction(data.messageId, data.emoji, data.count, data.reacted);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        message: describeError(error, "Could not react right now."),
      };
    }
  }

  // -------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------

  private onSignal(signal: TopicSignal): void {
    if ("resync" in signal) {
      void this.refresh();
      return;
    }
    switch (signal.type) {
      case "chat.message": {
        const parsed = messageEventSchema.safeParse(signal.payload);
        if (parsed.success) {
          this.upsertMessage(this.toMessage(parsed.data.message), {
            countUnread: true,
          });
        }
        return;
      }
      case "chat.message.deleted": {
        const parsed = deletedEventSchema.safeParse(signal.payload);
        if (parsed.success) this.applyDeleted(parsed.data.messageId);
        return;
      }
      case "chat.reaction": {
        const parsed = reactionEventSchema.safeParse(signal.payload);
        if (!parsed.success) return;
        const { messageId, emoji, count, playerId } = parsed.data;
        // Own reactions were already applied from the POST response.
        if (playerId !== null && playerId === this.selfPlayerId) return;
        this.applyReaction(messageId, emoji, count, undefined);
        return;
      }
      case "chat.pinned": {
        const parsed = pinnedEventSchema.safeParse(signal.payload);
        if (parsed.success) {
          this.patch({
            pinnedMessage: parsed.data.message
              ? this.toMessage(parsed.data.message)
              : null,
          });
        }
        return;
      }
      case "chat.presence": {
        const parsed = presenceEventSchema.safeParse(signal.payload);
        if (parsed.success) this.patch({ onlineCount: parsed.data.online });
        return;
      }
      default:
        // Additive contract: unknown event types are ignored.
        return;
    }
  }

  private async refresh(): Promise<void> {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      const data = await apiGet("/chat", snapshotSchema);
      this.selfPlayerId = data.viewer.playerId;
      this.selfDisplayName = data.viewer.displayName;
      this.patch({
        status: "connected",
        errorMessage: null,
        messages: data.messages.map((m) => this.toMessage(m)),
        canSend: data.viewer.canSend,
        banned: data.viewer.banned,
        hasOlder: data.hasOlder,
        onlineCount: data.onlineCount,
        pinnedMessage: data.pinned ? this.toMessage(data.pinned) : null,
        wagerGate:
          data.viewer.wagerLocked && data.viewer.wagerProgress
            ? data.viewer.wagerProgress
            : null,
      });
      this.startRateLimitCountdown(data.viewer.rateLimitSeconds);
    } catch (error) {
      this.patch({
        status: "error",
        errorMessage: describeError(error, "Chat is temporarily unavailable."),
      });
      this.scheduleRetry();
    } finally {
      this.refreshing = false;
    }
  }

  private scheduleRetry(): void {
    if (!this.connected || this.retryTimer !== null) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      void this.refresh();
    }, ERROR_RETRY_MS);
  }

  private toMessage(server: ServerMessage): ChatMessage {
    return {
      id: server.id,
      type: server.type,
      displayName: server.displayName,
      username: server.username,
      text: server.text,
      timestamp: server.timestamp,
      avatarUrl: server.avatarUrl ?? null,
      avatarBorderUrl: server.avatarBorderUrl ?? null,
      nameColor: server.nameColor ?? null,
      tierIconUrl: server.tierIconUrl ?? null,
      badges: server.badges ?? [],
      title: server.title ?? null,
      isStaff: server.isStaff ?? false,
      isSelf: server.playerId !== null && server.playerId === this.selfPlayerId,
      mentionedViewer: this.isMention(server.text),
      deleted: server.deleted,
      replyTo: server.replyTo ?? null,
      reactions: server.reactions ?? [],
      gameName: server.gameName ?? null,
      gameImageUrl: server.gameImageUrl ?? null,
      gameHref: server.gameHref ?? null,
      multiplier: server.multiplier ?? null,
    };
  }

  private isMention(text: string): boolean {
    if (!this.selfDisplayName) return false;
    return text
      .toLowerCase()
      .includes(`@${this.selfDisplayName.toLowerCase()}`);
  }

  private upsertMessage(
    message: ChatMessage,
    { countUnread }: { countUnread: boolean },
  ): void {
    const exists = this.snapshot.messages.some((m) => m.id === message.id);
    let messages = exists
      ? this.snapshot.messages.map((m) => (m.id === message.id ? message : m))
      : [...this.snapshot.messages, message];
    if (!exists && messages.length > MESSAGE_CAP) {
      messages = messages.slice(messages.length - MESSAGE_CAP);
    }
    const counts: Partial<ChatSnapshot> = {};
    if (countUnread && !exists && !message.isSelf && message.type === "chat") {
      counts.unreadCount = this.snapshot.unreadCount + 1;
      if (this.isMention(message.text)) {
        counts.unreadMentions = this.snapshot.unreadMentions + 1;
      }
    }
    this.patch({ messages, ...counts });
  }

  private applyDeleted(messageId: string): void {
    const tombstone = (message: ChatMessage): ChatMessage => {
      let next = message;
      if (message.id === messageId) {
        next = { ...next, deleted: true, text: "" };
      }
      if (message.replyTo && message.replyTo.id === messageId) {
        next = {
          ...next,
          replyTo: { ...message.replyTo, deleted: true, excerpt: "" },
        };
      }
      return next;
    };
    this.patch({
      messages: this.snapshot.messages.map(tombstone),
      ...(this.snapshot.pinnedMessage?.id === messageId
        ? { pinnedMessage: null }
        : {}),
    });
  }

  private applyReaction(
    messageId: string,
    emoji: string,
    count: number,
    reactedByViewer: boolean | undefined,
  ): void {
    const update = (message: ChatMessage): ChatMessage => {
      if (message.id !== messageId) return message;
      const existing = message.reactions ?? [];
      const previous = existing.find((r) => r.emoji === emoji);
      const withoutEmoji = existing.filter((r) => r.emoji !== emoji);
      const reactions =
        count <= 0
          ? withoutEmoji
          : [
              ...withoutEmoji,
              {
                emoji,
                count,
                reactedByViewer:
                  reactedByViewer ?? previous?.reactedByViewer ?? false,
              },
            ];
      return { ...message, reactions };
    };
    this.patch({
      messages: this.snapshot.messages.map(update),
      ...(this.snapshot.pinnedMessage?.id === messageId
        ? { pinnedMessage: update(this.snapshot.pinnedMessage) }
        : {}),
    });
  }

  private describeSendError(error: unknown): string {
    if (!(error instanceof ApiError)) return "Could not send right now.";
    switch (error.code) {
      case "CHAT_RATE_LIMITED": {
        const retry = Number(error.details.retryAfterSeconds ?? 0) || 1;
        this.startRateLimitCountdown(retry);
        return `Slow mode — wait ${retry}s and try again.`;
      }
      case "CHAT_BANNED":
        this.patch({ banned: true, canSend: false });
        return "You have been banned from chat.";
      case "SESSION_MISSING":
        return "Sign in to chat.";
      case "CHAT_CLOSED":
        return "Chat is closed right now.";
      default:
        return error.message || "Could not send right now.";
    }
  }

  private startRateLimitCountdown(seconds: number): void {
    this.stopRateLimitCountdown();
    const initial = Math.max(0, Math.ceil(seconds));
    this.patch({ rateLimitSeconds: initial });
    if (initial <= 0) return;
    this.rateLimitTimer = window.setInterval(() => {
      const next = this.snapshot.rateLimitSeconds - 1;
      if (next <= 0) this.stopRateLimitCountdown();
      this.patch({ rateLimitSeconds: Math.max(0, next) });
    }, 1_000);
  }

  private stopRateLimitCountdown(): void {
    if (this.rateLimitTimer !== null) {
      window.clearInterval(this.rateLimitTimer);
      this.rateLimitTimer = null;
    }
  }

  private patch(partial: Partial<ChatSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...partial };
    for (const listener of this.listeners) listener();
  }
}

export const chatAdapter: ChatAdapter = new ChatBackendAdapter();
